import { DEFAULT_CURRENCY_CODE, Inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import {
    BehaviorSubject,
    combineLatest,
    defer,
    EMPTY,
    firstValueFrom,
    from,
    Observable,
    ReplaySubject,
    Subject,
} from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { ArlCalculationSurcharge } from '../../dave-reports/components/arl-calculation/arl-calculation-surcharge';
import {
    _getArlChildren,
    ARL_DEFAULT_VALUES,
    baseCostFromFormValue, getARLCostAmountWithAllowance,
} from '../../dave-reports/components/arl-calculation/arl-calculation.helper';
import { arlTableDataArlForm } from '../../dave-reports/components/arl-calculation/arl-form-data.service';
import {
    calculationArlForm,
    calculationArlFormGroup,
} from '../../dave-reports/components/arl-calculation/calculation-arl-form-data.service';
import {
    calculationArlTemplateForm,
} from '../../dave-reports/components/arl-calculation/calculation-arl-template-form-data.service';
import {
    isNotNullOrUndefined,
    PickCommon,
    stringifyIfNotNullOrUndefined,
    stringifyIfNotUndefined,
} from '../../helper/helper';
import { ARLCustomProperties } from '../entities/accounts-receivable-ledger-template.entity';
import {
    AccountsReceivableLedgerEntity,
    AccountsReceivableLedgerEntityFromBackend,
    AccountsReceivableLedgerTypeEnum,
    financialRound,
} from '../entities/accounts-receivable-ledger.entity';
import { LedgerImportARLCalculationPreChargeByType, LedgerImportDocumentTypes } from '../entities/ledger-import.entity';
import {
    AccountsReceivableLedgerPatchType,
    AccountsReceivableLedgerPostType,
    DaveMutationCreateAccountsReceivableLedgerFromCommissionArgs,
    QueryAccountsReceivableLedgerArgs,
} from '../graphql-types';
import { AccountsReceivableLedgerResolver } from '../guards/accounts-receivable-ledger.resolver';
import { BusinessVolumeResolver } from '../guards/business-volume.resolver';
import { FrontendDate, useGzip } from '../helper/backend-frontend-conversion.helper';
import { getFetched$ } from '../helper/helper';
import { getLatestUpdatedAt } from '../helper/reducer.helper';
import { State } from '../State';
import { AccountsReceivableLedgerActionTypes } from '../State/actions/accounting.actions';
import { BaseActionTypes } from '../State/actions/base.actions';
import {
    getAccountsReceivableLedgerDictionary,
    getAccountsReceivableLedgers,
    getAccountsReceivableLedgersFetched,
} from '../State/selectors/accounting.selector';
import { getBusinessVolumeFetched } from '../State/selectors/business-volume.selector';
import { getLedgerImportDictionary, getLedgerImportsFetched } from '../State/selectors/ledger-import.selector';
import { HttpService } from './http.service';
import { MaterialToSupplierEntity } from '../entities/materialToSupplier.entity';

export const arlPatchQuery = (Payload: AccountsReceivableLedgerPatchType, currencyCalcFactor = 100) => {
    return `{
         id: ${Payload.id}
    ${stringifyIfNotNullOrUndefined(Payload, 'date')}
    ${isNotNullOrUndefined(Payload.amount) ? 'amount:' + (Payload.amount * currencyCalcFactor).toFixed(0) : ''}
    ${stringifyIfNotNullOrUndefined(Payload, 'accountNo')}
    ${stringifyIfNotNullOrUndefined(Payload, 'buCode')}
    ${stringifyIfNotNullOrUndefined(Payload, 'costCategoryId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'costCategoryId2')}
    ${stringifyIfNotNullOrUndefined(Payload, 'tax')}
    ${stringifyIfNotNullOrUndefined(Payload, 'information')}
    ${stringifyIfNotNullOrUndefined(Payload, 'currencyCode')}
    ${stringifyIfNotNullOrUndefined(Payload, 'invoiceId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'bookingText')}
    ${stringifyIfNotNullOrUndefined(Payload, 'partyId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'paidAt')}
    ${stringifyIfNotNullOrUndefined(Payload, 'iban')}
    ${stringifyIfNotNullOrUndefined(Payload, 'dueDate')}
    ${stringifyIfNotNullOrUndefined(Payload, 'customerName')}
    ${stringifyIfNotNullOrUndefined(Payload, 'customerCity')}
    ${isNotNullOrUndefined(Payload.costAmount) ? 'costAmount:' + financialRound(Payload.costAmount * currencyCalcFactor, AccountsReceivableLedgerEntity.CostAmountDecimalPlaces) : ''}
    ${isNotNullOrUndefined(Payload.discountAmount) ? 'discountAmount:' + (Payload.discountAmount * currencyCalcFactor).toFixed(0) : ''}
    ${stringifyIfNotNullOrUndefined(Payload, 'internalInvoiceId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'vatId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'exchangeRate')}
    ${stringifyIfNotNullOrUndefined(Payload, 'bankCode')}
    ${stringifyIfNotNullOrUndefined(Payload, 'bankAccount')}
    ${stringifyIfNotNullOrUndefined(Payload, 'bankCountry')}
    ${stringifyIfNotNullOrUndefined(Payload, 'swiftCode')}
    ${stringifyIfNotNullOrUndefined(Payload, 'accountName')}
    ${stringifyIfNotNullOrUndefined(Payload, 'paymentConditionsId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'discountPercentage')}
    ${stringifyIfNotNullOrUndefined(Payload, 'discountPaymentDate')}
    ${Payload.type ? `type: ${Payload.type}` : ''}
    ${isNotNullOrUndefined(Payload.discountAmount2) ? 'discountAmount2:' + (Payload.discountAmount2 * currencyCalcFactor).toFixed(0) : ''}
    ${stringifyIfNotNullOrUndefined(Payload, 'discountPercentage2')}
    ${stringifyIfNotNullOrUndefined(Payload, 'discountPaymentDate2')}
    ${stringifyIfNotNullOrUndefined(Payload, 'bpAccountNo')}
    ${stringifyIfNotNullOrUndefined(Payload, 'deliveryDate')}
    ${stringifyIfNotNullOrUndefined(Payload, 'orderId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'ledgerImportId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'customerId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'commissionId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'eventId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'generatedDocumentId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'canBeEdited')}
    ${stringifyIfNotNullOrUndefined(Payload, 'quantity')}
    ${isNotNullOrUndefined(Payload.baseCost) ? 'baseCost:' + (Payload.baseCost * currencyCalcFactor).toFixed(0) : ''}
    ${isNotNullOrUndefined(Payload.lohnBaseCostPart) ? 'lohnBaseCostPart:' + (Payload.lohnBaseCostPart * currencyCalcFactor).toFixed(0) : ''}
    ${isNotNullOrUndefined(Payload.payedAmount) ? 'payedAmount:' + (Payload.payedAmount * currencyCalcFactor).toFixed(0) : ''}
    multiplikator: ${JSON.stringify(isNotNullOrUndefined(Payload.multiplikator) ? Payload.multiplikator : 1)}
    ${stringifyIfNotNullOrUndefined(Payload, 'customProperties')}
    ${stringifyIfNotNullOrUndefined(Payload, 'documentType')}
    ${stringifyIfNotNullOrUndefined(Payload, 'partnerOfficeId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'arlIds')}
    ${stringifyIfNotUndefined(Payload, 'parentId')}
    ${stringifyIfNotUndefined(Payload, 'quantityTypeId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'inheritFromChildren')}
    ${stringifyIfNotNullOrUndefined(Payload, 'isVisible')}
    ${stringifyIfNotUndefined(Payload, 'resourceId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'longInformation')}
    ${stringifyIfNotNullOrUndefined(Payload, 'showLongInformation')}
    ${stringifyIfNotUndefined(Payload, 'jobSpecificationId')}
    ${stringifyIfNotUndefined(Payload, 'materialId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'isOptional')}
    ${stringifyIfNotNullOrUndefined(Payload, 'isCrossedOut')}
    ${stringifyIfNotUndefined(Payload, 'itemNumber')}
    ${stringifyIfNotNullOrUndefined(Payload, 'hideInGroupSummary')}
    ${Payload.supplierId !== undefined ? ('supplierId: ' + Payload.supplierId) : ''}
    ${stringifyIfNotNullOrUndefined(Payload, 'startOnNewPage')}
    ${
        Payload.baseCostAllowances
            ? 'baseCostAllowances: [' +
              Payload.baseCostAllowances
                  .map(
                      (a) => `{
    ${stringifyIfNotUndefined(a, 'type')}
    ${stringifyIfNotUndefined(a, 'title')}
    ${stringifyIfNotUndefined(a, 'percent')}
    }`,
                  )
                  .join(', ') +
              ']'
            : ''
    }
    ${stringifyIfNotUndefined(Payload, 'costAmountAllowance')}
    ${stringifyIfNotUndefined(Payload, 'showQuantityHistory')}
    ${stringifyIfNotUndefined(Payload, 'useQuantityHistory')}
    ${
        Payload.quantityHistories
            ? 'quantityHistories: [' +
              Payload.quantityHistories
                  .map(
                      (a) => `{
    ${stringifyIfNotUndefined(a, 'date')}
    ${stringifyIfNotUndefined(a, 'quantityDeltaFormula')}

    ${stringifyIfNotUndefined(a, 'quantityDelta')}
    }`,
                  )
                  .join(', ') +
              ']'
            : ''
    }

    ${stringifyIfNotUndefined(Payload, 'lVFlEk')}
    ${stringifyIfNotUndefined(Payload, 'lVGEK')}
    ${stringifyIfNotUndefined(Payload, 'lVMinutes')}
    ${stringifyIfNotUndefined(Payload, 'lVSoAntEk')}
    ${stringifyIfNotUndefined(Payload, 'lVSoEk')}
    ${stringifyIfNotUndefined(Payload, 'lvSoEkRabatt')}
    ${stringifyIfNotUndefined(Payload, 'lvPartieHEur')}
    ${isNotNullOrUndefined(Payload.baseCostGross) ? 'baseCostGross:' + (Payload.baseCostGross * currencyCalcFactor).toFixed(0) : ''}
    ${stringifyIfNotUndefined(Payload, 'quantityFormula')}
    ${stringifyIfNotUndefined(Payload, 'priceComparisonId')}
    ${stringifyIfNotUndefined(Payload, 'changedAfterSelectedFromPriceComparison')}
    ${
        Payload.gapPatches
            ? 'gapPatches: [' +
            Payload.gapPatches
                .map(
                    (a) => `{
                    ${stringifyIfNotUndefined(a, 'id')}
    ${stringifyIfNotUndefined(a, 'caption')}
    ${stringifyIfNotUndefined(a, 'body')}
    ${stringifyIfNotUndefined(a, 'indexInLongText')}
    }`,
                )
                .join(', ') +
            ']'
            : ''
    }
    ${stringifyIfNotUndefined(Payload, 'surchargeOverride')}
    ${stringifyIfNotNullOrUndefined(Payload, 'fixedPrices')}
  }`;
};

export const arlPostQuery = (Payload: AccountsReceivableLedgerPostType, currencyCalcFactor = 100) => {
    return `{
        ${stringifyIfNotNullOrUndefined(Payload, 'date')}
    ${isNotNullOrUndefined(Payload.amount) ? 'amount:' + (Payload.amount * currencyCalcFactor).toFixed(0) : ''}
    ${stringifyIfNotNullOrUndefined(Payload, 'accountNo')}
    ${stringifyIfNotNullOrUndefined(Payload, 'buCode')}
    ${stringifyIfNotNullOrUndefined(Payload, 'costCategoryId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'costCategoryId2')}
    ${stringifyIfNotNullOrUndefined(Payload, 'tax')}
    ${stringifyIfNotNullOrUndefined(Payload, 'information')}
    ${stringifyIfNotNullOrUndefined(Payload, 'currencyCode')}
    ${stringifyIfNotNullOrUndefined(Payload, 'invoiceId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'bookingText')}
    ${stringifyIfNotNullOrUndefined(Payload, 'partyId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'paidAt')}
    ${stringifyIfNotNullOrUndefined(Payload, 'iban')}
    ${stringifyIfNotNullOrUndefined(Payload, 'dueDate')}
    ${stringifyIfNotNullOrUndefined(Payload, 'customerName')}
    ${stringifyIfNotNullOrUndefined(Payload, 'customerCity')}
    ${isNotNullOrUndefined(Payload.costAmount) ? 'costAmount:' + financialRound(Payload.costAmount * currencyCalcFactor, AccountsReceivableLedgerEntity.CostAmountDecimalPlaces) : ''}
    ${isNotNullOrUndefined(Payload.discountAmount) ? 'discountAmount:' + (Payload.discountAmount * currencyCalcFactor).toFixed(0) : ''}
    ${stringifyIfNotNullOrUndefined(Payload, 'internalInvoiceId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'vatId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'exchangeRate')}
    ${stringifyIfNotNullOrUndefined(Payload, 'bankCode')}
    ${stringifyIfNotNullOrUndefined(Payload, 'bankAccount')}
    ${stringifyIfNotNullOrUndefined(Payload, 'bankCountry')}
    ${stringifyIfNotNullOrUndefined(Payload, 'swiftCode')}
    ${stringifyIfNotNullOrUndefined(Payload, 'accountName')}
    ${stringifyIfNotNullOrUndefined(Payload, 'paymentConditionsId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'discountPercentage')}
    ${stringifyIfNotNullOrUndefined(Payload, 'discountPaymentDate')}
    ${isNotNullOrUndefined(Payload.discountAmount2) ? 'discountAmount2:' + (Payload.discountAmount2 * currencyCalcFactor).toFixed(0) : ''}
    ${stringifyIfNotNullOrUndefined(Payload, 'discountPercentage2')}
    ${stringifyIfNotNullOrUndefined(Payload, 'discountPaymentDate2')}
    ${stringifyIfNotNullOrUndefined(Payload, 'bpAccountNo')}
    ${stringifyIfNotNullOrUndefined(Payload, 'deliveryDate')}
    ${stringifyIfNotNullOrUndefined(Payload, 'orderId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'ledgerImportId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'customerId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'commissionId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'eventId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'generatedDocumentId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'canBeEdited')}
    ${stringifyIfNotNullOrUndefined(Payload, 'quantity')}
    ${isNotNullOrUndefined(Payload.baseCost) ? 'baseCost:' + (Payload.baseCost * currencyCalcFactor).toFixed(0) : ''}
    ${isNotNullOrUndefined(Payload.lohnBaseCostPart) ? 'lohnBaseCostPart:' + (Payload.lohnBaseCostPart * currencyCalcFactor).toFixed(0) : ''}
    ${stringifyIfNotNullOrUndefined(Payload, 'quantityTypeId')}
    multiplikator: ${JSON.stringify(isNotNullOrUndefined(Payload.multiplikator) ? Payload.multiplikator : 1)}
    ${stringifyIfNotNullOrUndefined(Payload, 'customProperties')}
    ${stringifyIfNotNullOrUndefined(Payload, 'documentType')}
    ${stringifyIfNotNullOrUndefined(Payload, 'partnerOfficeId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'parentId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'isVisible')}
    ${stringifyIfNotUndefined(Payload, 'resourceId')}
    ${stringifyIfNotUndefined(Payload, 'materialId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'longInformation')}
    ${stringifyIfNotNullOrUndefined(Payload, 'showLongInformation')}
    ${stringifyIfNotNullOrUndefined(Payload, 'inheritFromChildren')}
    ${stringifyIfNotUndefined(Payload, 'jobSpecificationId')}
    ${stringifyIfNotNullOrUndefined(Payload, 'isOptional')}
    ${stringifyIfNotNullOrUndefined(Payload, 'isCrossedOut')}
    ${stringifyIfNotNullOrUndefined(Payload, 'itemNumber')}
    ${stringifyIfNotNullOrUndefined(Payload, 'hideInGroupSummary')}
    ${stringifyIfNotNullOrUndefined(Payload, 'supplierId')}
    ${Payload.type ? `type: ${Payload.type}` : ''}
    ${
        Payload.baseCostAllowances
            ? 'baseCostAllowances: [' +
              Payload.baseCostAllowances
                  .map(
                      (a) => `{
    ${stringifyIfNotUndefined(a, 'type')}
    ${stringifyIfNotUndefined(a, 'title')}
    ${stringifyIfNotUndefined(a, 'percent')}
    }`,
                  )
                  .join(', ') +
              ']'
            : ''
    }
    ${stringifyIfNotUndefined(Payload, 'costAmountAllowance')}
    ${stringifyIfNotUndefined(Payload, 'showQuantityHistory')}
    ${stringifyIfNotUndefined(Payload, 'useQuantityHistory')}
    ${
        Payload.quantityHistories
            ? 'quantityHistories: [' +
              Payload.quantityHistories
                  .map(
                      (a) => `{
    ${stringifyIfNotUndefined(a, 'date')}
    ${stringifyIfNotUndefined(a, 'quantityDeltaFormula')}
    ${stringifyIfNotUndefined(a, 'quantityDelta')}
    }`,
                  )
                  .join(', ') +
              ']'
            : ''
    }

    ${stringifyIfNotUndefined(Payload, 'lVFlEk')}
    ${stringifyIfNotUndefined(Payload, 'lVGEK')}
    ${stringifyIfNotUndefined(Payload, 'lVMinutes')}
    ${stringifyIfNotUndefined(Payload, 'lVSoAntEk')}
    ${stringifyIfNotUndefined(Payload, 'lVSoEk')}
    ${stringifyIfNotUndefined(Payload, 'lvSoEkRabatt')}
    ${stringifyIfNotUndefined(Payload, 'lvPartieHEur')}
    ${isNotNullOrUndefined(Payload.baseCostGross) ? 'baseCostGross:' + (Payload.baseCostGross * currencyCalcFactor).toFixed(0) : ''}
    ${stringifyIfNotUndefined(Payload, 'quantityFormula')}
    ${
        Payload.gaps
            ? 'gaps: [' +
            Payload.gaps
                .map(
                    (a) => `{
    ${stringifyIfNotUndefined(a, 'caption')}
    ${stringifyIfNotUndefined(a, 'body')}
    ${stringifyIfNotUndefined(a, 'indexInLongText')}
    }`,
                )
                .join(', ') +
            ']'
            : ''
    }
    ${stringifyIfNotUndefined(Payload, 'surchargeOverride')}
    ${stringifyIfNotNullOrUndefined(Payload, 'fixedPrices')}
    ${stringifyIfNotNullOrUndefined(Payload, 'arlIds')}
}`;
};
export type withChildren<T> = T&{children: withChildren<T>[]};

enum ErrorCodes {
    Add = 'Buchungsposition hinzufügen fehlgeschlagen',
    Modify = 'Buchungsposition ändern fehlgeschlagen',
    Delete = 'Buchungsposition löschen fehlgeschlagen',
    Load = 'Buchungsposition Abrufen fehlgeschlagen',
}
@Injectable({
    providedIn: 'root',
})
export class ArlDataService {
    private arlsMap$ = new BehaviorSubject(new Map<number, AccountsReceivableLedgerEntity>());
    private arlsCommissionLastUpdatedAt = new Map<number, Date>();
    private arlsLedgerImportLastUpdatedAt = new Map<number, Date>();

    constructor(
        private store$: Store<State>,
        private gatewayHttpService: HttpService,
        private bvResolver: BusinessVolumeResolver,
        private arlResolver: AccountsReceivableLedgerResolver,
        private actions: Actions,
        @Inject(DEFAULT_CURRENCY_CODE) private defaultCurrencyCode: string,
    ) {
        // this.arlsMap$.subscribe((arlsMap) => {
        //     console.log('arlsMap', arlsMap);
        // });
    }

    public async addArlsWithChildren(payload: Array<withChildren<AccountsReceivableLedgerPostType>>) {
        // const getChildPayload = (
        //     sourceChildArl: typeof sourceArl,
        //     valuesMissingInForm: Pick<AccountsReceivableLedgerPostType, 'isVisible' | 'multiplikator' | 'itemNumber' | 'ledgerImportId'>,
        //     zuschlForCalculationArl?: ArlCalculationSurcharge<calculationArlForm | calculationArlTemplateForm>[],
        //     individuallyCostCalculation?: boolean,
        // ): AccountsReceivableLedgerPostType => {
        //     const cp: ARLCustomProperties = {
        //         // individuallyCostCalculation: sourceChildArl.IndividuallyCostCalculation,
        //     };
        //     if (isNotNullOrUndefined(individuallyCostCalculation)) {
        //         cp.individuallyCostCalculation = individuallyCostCalculation;
        //     }
        //     return {
        //         itemNumber: valuesMissingInForm.itemNumber,
        //         quantity: sourceChildArl.Quantity || 0,
        //         tax: isNotNullOrUndefined(sourceChildArl.Tax) ? sourceChildArl.Tax : defaultTax, // fix fürn nicht angelegte kalkulierte arls
        //         // currencyCode: sourceChildArl.CurrencyCode,
        //         bookingText: sourceChildArl.BookingText,
        //         baseCost: zuschlForCalculationArl ? baseCostFromFormValue(sourceChildArl, zuschlForCalculationArl) : sourceChildArl.BaseCost || 0,
        //         baseCostGross: sourceChildArl.BaseCostGross,
        //         costAmount: sourceChildArl.CostAmount,
        //         ledgerImportId: valuesMissingInForm.ledgerImportId,
        //         // documentType: sourceChildArl.DocumentType,
        //         // customerId: sourceChildArl.CustomerId,
        //         // commissionId: sourceChildArl.CommissionId,
        //         information: sourceChildArl.Information,
        //         quantityTypeId: sourceChildArl.QuantityTypeId,
        //         multiplikator: valuesMissingInForm.multiplikator, //todo multiplikator ganz ausbauen oder in form einbauen //sourceChildArl.Multiplier,
        //         customProperties: JSON.stringify({
        //             cp,
        //             // ...sourceChildArl.CustomProperties,
        //             // rootTemplateId: sourceChildArl.a,
        //         }),
        //         isVisible: valuesMissingInForm.isVisible,
        //         longInformation: sourceChildArl.Longtext,
        //         showLongInformation: sourceChildArl.ShowLongtext,
        //         // parentId: parentId,
        //         type: sourceChildArl.Type as any,
        //         resourceId: sourceChildArl.ResourceId,
        //         materialId: sourceChildArl.MaterialId,
        //         jobSpecificationId: sourceChildArl.JobSpecificationId,
        //         isOptional: sourceChildArl.IsOptional,
        //         isCrossedOut: sourceChildArl.CrossedOut,
        //         hideInGroupSummary: sourceChildArl.HideInGroupSummary,
        //         baseCostAllowances: sourceChildArl.BaseCostAllowances?.map((a) => ({
        //             percent: a.percent,
        //             type: a.arlType,
        //             title: a.title,
        //         })),
        //         inheritFromChildren: sourceChildArl.InheritFromChildren,
        //         quantityFormula: sourceChildArl.QuantityFormula,
        //     };
        // };
        // payload.map(async sourceArl => {
            const childsToAdd: AccountsReceivableLedgerPostType[] = [];
            const childsToAddSources: typeof payload = [];

            function addChilds(source: typeof payload[0]) {
                childsToAdd.push(source);
                childsToAddSources.push(source);
                source.children?.forEach((child) => {
                    addChilds(child);
                });
            }

            payload.forEach(sourceArl => addChilds(sourceArl));
            const responseArls = await this.addArls(childsToAdd);
            const responseArlsIds = responseArls.map((r) => r.Id);

            console.log({responseArls, childsToAdd, childsToAddSources,});

            const arlsToUpdate: AccountsReceivableLedgerPatchType[] = [];
            childsToAddSources.forEach((src, i) => {
                const childCount = (src.children?.length || 0);
                if (childCount > 0) {
                    // const targetChildIds = responseArlsIds.slice(i + 1, i + 1 + childCount); // hier sind die falschen children drin wenn die auch unterchildren haben
                    const targetChildIds = childsToAddSources
                        .filter((p) => src.children.includes(p))
                        .map(p => responseArlsIds[childsToAdd.indexOf(p)]);

                    const targetId = responseArlsIds[i];
                    console.log({targetChildIds, targetId})
                    const payloadExists = arlsToUpdate.find((a) => a.id === targetId);
                    if (payloadExists) {
                        payloadExists.arlIds = targetChildIds;
                    } else {
                        arlsToUpdate.push({
                            id: targetId,
                            arlIds: targetChildIds,
                            multiplikator: responseArls[i].Multiplier,
                        });
                    }
                    targetChildIds.forEach((cId) => {
                        arlsToUpdate.push({
                            id: cId,
                            parentId: targetId,
                            multiplikator: responseArls.find((r) => r.Id === cId)?.Multiplier,
                        });
                    });
                }
            });
            const changedArls = arlsToUpdate.length ? await this.changeArls(arlsToUpdate) : [];
            return responseArls.map((r, i) => changedArls.find((c) => c.Id === r.Id) || r);
        //     }
        // )
    }
    public addArls(payload: AccountsReceivableLedgerPostType[], currencyCalcFactor = 100): Promise<AccountsReceivableLedgerEntity[]> {
        console.log({ payload, currencyCalcFactor });

        const queryString = `mutation {createAccountsReceivableLedgerMultiple(posts: [
        ${payload.map((Payload) => arlPostQuery(Payload, currencyCalcFactor)).join(', ')}
        ]) {${AccountsReceivableLedgerEntity.GqlFields.join(',')}}}`;
        return firstValueFrom(
            this.gatewayHttpService.graphQl({ query: queryString }).pipe(
                withLatestFrom(this.store$.select(getBusinessVolumeFetched)),
                map(([res, bvFetched]) => {
                    if (bvFetched) {
                        this.bvResolver.pollUpdated();
                    }
                    if (res?.createAccountsReceivableLedgerMultiple?.every((r) => !!r)) {
                        const entitys = res.createAccountsReceivableLedgerMultiple.map((r) => AccountsReceivableLedgerEntityFromBackend(r));
                        this.store$.dispatch(AccountsReceivableLedgerActionTypes.UpdateMany({ Payload: entitys }));
                        return entitys;
                    } else {
                        throw 'wrong response';
                    }
                }),
                catchError((err, caught) => {
                    this.store$.dispatch(
                        BaseActionTypes.ErrorAction({
                            Payload: {
                                ToasterMessage: ErrorCodes.Add,
                                Err: err,
                                Caught: caught,
                            },
                        }),
                    );
                    return EMPTY;
                }),
            ),
        );
    }

    public async getARLsByUpdatedSince(updatedSince: Date) {
        const result = await this.getARLs({ updatedSince: FrontendDate(updatedSince) });
        if (result) {
            this.store$.dispatch(
                AccountsReceivableLedgerActionTypes.UpdateMany({
                    Payload: result,
                }),
            );
        }
    }

    public createArlsFromCommission(payload: DaveMutationCreateAccountsReceivableLedgerFromCommissionArgs): Promise<AccountsReceivableLedgerEntity[]> {
        const queryString = `mutation {
        createAccountsReceivableLedgerFromCommission(
            commissionId: ${payload.commissionId},
            includeMaterials: ${payload.includeMaterials ?? false},
            includeResources: ${payload.includeResources ?? false},
            includeConstructionDiarys: ${payload.includeConstructionDiarys ?? false},
            includeWorkedTimes: ${payload.includeWorkedTimes ?? false}
            quantitiesFromMaterialList: ${payload.quantitiesFromMaterialList ?? false}
        ) {
            ${AccountsReceivableLedgerEntity.GqlFields.join(',')}
        }
    }`;
        return firstValueFrom(
            this.gatewayHttpService.graphQl({ query: queryString }).pipe(
                map((res) => {
                    if (res?.createAccountsReceivableLedgerFromCommission?.every((r) => !!r)) {
                        const entitys = res.createAccountsReceivableLedgerFromCommission.map((r) => AccountsReceivableLedgerEntityFromBackend(r));
                        this.store$.dispatch(
                            AccountsReceivableLedgerActionTypes.UpdateMany({
                                Payload: entitys,
                            }),
                        );
                        return entitys;
                    } else {
                        throw 'wrong response';
                    }
                }),
                catchError((err, caught) => {
                    this.store$.dispatch(
                        BaseActionTypes.ErrorAction({
                            Payload: {
                                ToasterMessage: ErrorCodes.Add,
                                Err: err,
                                Caught: caught,
                            },
                        }),
                    );
                    return EMPTY;
                }),
            ),
        );
    }

    public changeArls(payload: AccountsReceivableLedgerPatchType[], currencyCalcFactor = 100): Promise<AccountsReceivableLedgerEntity[]> {
        console.log({ payload, currencyCalcFactor });

        const queryString = `mutation {changeAccountsReceivableLedgerMultiple(patches: [
        ${payload.map((Payload) => arlPatchQuery(Payload, currencyCalcFactor)).join(', ')}
        ]) {${AccountsReceivableLedgerEntity.GqlFields.join(',')}}}`;

        const options = {
            useCompression: useGzip(),
        };
        return firstValueFrom(
            this.gatewayHttpService.graphQl({ query: queryString }, options).pipe(
                withLatestFrom(this.store$.select(getBusinessVolumeFetched)),
                map(([res, bvFetched]) => {
                    if (bvFetched) {
                        this.bvResolver.pollUpdated();
                    }
                    if (res?.changeAccountsReceivableLedgerMultiple?.every((r) => !!r)) {
                        const entitys = res.changeAccountsReceivableLedgerMultiple.map((r) => AccountsReceivableLedgerEntityFromBackend(r));
                        this.store$.dispatch(
                            AccountsReceivableLedgerActionTypes.UpdateMany({
                                Payload: entitys,
                            }),
                        );
                        return entitys;
                    } else {
                        throw 'wrong response';
                    }
                }),
                catchError((err, caught) => {
                    this.store$.dispatch(
                        BaseActionTypes.ErrorAction({
                            Payload: {
                                ToasterMessage: ErrorCodes.Modify,
                                Err: err,
                                Caught: caught,
                            },
                        }),
                    );
                    return EMPTY;
                }),
            ),
        );
    }

    public removeArl(id: number) {
        const resolved = firstValueFrom(this.actions.pipe(ofType(AccountsReceivableLedgerActionTypes.DeleteAccountsReceivableLedgerSuccess, BaseActionTypes.ErrorAction))).then((action) => {
            if (action.type === BaseActionTypes.ErrorAction.type) {
                throw ErrorCodes.Delete;
            }
            return { deletedId: id };
        });
        this.store$.dispatch(AccountsReceivableLedgerActionTypes.DeleteAccountsReceivableLedger({ Payload: { id } }));
        return resolved;
    }

    public async getARLs(payload: QueryAccountsReceivableLedgerArgs) {
        let page = 1;
        let pageCount = 1;

        const result: AccountsReceivableLedgerEntity[] = [];
        let error = false;

        while (pageCount >= page && !error) {
            const queryString = `query {
          accountsReceivableLedger(
          page: ${page}
          ${stringifyIfNotNullOrUndefined(payload, 'updatedSince')}
          ${stringifyIfNotNullOrUndefined(payload, 'commissionId')}
          ${stringifyIfNotNullOrUndefined(payload, 'ledgerImportIdIsNull')}
          ${stringifyIfNotNullOrUndefined(payload, 'parentIdIsNull')}
          ${stringifyIfNotNullOrUndefined(payload, 'ledgerImportId')}
          ${stringifyIfNotNullOrUndefined(payload, 'ledgerImportIds')}
          ) {pageCount
    items { ${AccountsReceivableLedgerEntity.GqlFields.join(',')} }}
        }
        `;
            await firstValueFrom(this.gatewayHttpService.graphQl({ query: queryString }, { retry: true }))
                .then((res) => {
                    page++;
                    if (res && res.accountsReceivableLedger && res.accountsReceivableLedger[0].items) {
                        if (res.accountsReceivableLedger[0].pageCount) {
                            pageCount = res.accountsReceivableLedger[0].pageCount;
                        }
                        result.push(...res.accountsReceivableLedger[0].items.map((f) => AccountsReceivableLedgerEntityFromBackend(f)));
                    } else {
                        error = true;
                        this.store$.dispatch(BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: ErrorCodes.Load } }));
                    }
                })
                .catch((err) => {
                    error = true;
                    this.store$.dispatch(
                        BaseActionTypes.ErrorAction({
                            Payload: {
                                ToasterMessage: ErrorCodes.Load,
                                Err: err,
                            },
                        }),
                    );
                });
        }

        return error ? null : result;
    }

    private async _fetchARlByLedgerImport(ledgerImportId: number) {
        let lastUpdatedAt: string | null = null;
        if (this.arlsLedgerImportLastUpdatedAt.has(ledgerImportId)) {
            lastUpdatedAt = FrontendDate(this.arlsLedgerImportLastUpdatedAt.get(ledgerImportId));
        }
        try {
            const result = await this.getARLs({
                ledgerImportId: ledgerImportId,
                updatedSince: lastUpdatedAt,
            });
            let m = this.arlsMap$.getValue();
            const filteredResult = result.filter((r) => {
                const v = m.get(r.Id);
                return !v || v.UpdatedAt.getTime() !== r.UpdatedAt.getTime();
            });
            if (filteredResult.length > 0) {
                filteredResult.forEach((arl) => {
                    m.set(arl.Id, arl);
                });
                this.arlsMap$.next(m);
                this.store$.dispatch(AccountsReceivableLedgerActionTypes.UpdateMany({ Payload: filteredResult }));
            }
            this.arlsLedgerImportLastUpdatedAt.set(ledgerImportId, getLatestUpdatedAt(result, this.arlsLedgerImportLastUpdatedAt.get(ledgerImportId)));
        } catch (e) {
            console.error(e);
        }
    }
    public async fetchARlByLedgerImports(ids: number[], reload = false) {
        const toFetch = reload
            ? ids
            : ids.filter((id) => !this.arlsLedgerImportLastUpdatedAt.has(id));

        if (!toFetch.length) return;

        await this._fetchARlByIds(toFetch);
    }

    private async _fetchARlByIds(ids: number[]) {
        let lastUpdatedAt: string | null = null;
        const lastDates = ids
            .map(id => this.arlsLedgerImportLastUpdatedAt.get(id))
            .filter(Boolean);

        if (lastDates.length > 0) {
            lastUpdatedAt = FrontendDate(
                new Date(Math.max(...lastDates.map(d => new Date(d!).getTime())))
            );
        }

        try {
            const result = await this.getARLs({
                ledgerImportIds: ids,
                updatedSince: lastUpdatedAt,
            });

            const m = this.arlsMap$.getValue();

            const filteredResult = result.filter((r) => {
                const v = m.get(r.Id);
                return !v || v.UpdatedAt.getTime() !== r.UpdatedAt.getTime();
            });

            if (filteredResult.length > 0) {
                filteredResult.forEach((arl) => {
                    m.set(arl.Id, arl);
                });

                this.arlsMap$.next(m);

                this.store$.dispatch(
                    AccountsReceivableLedgerActionTypes.UpdateMany({
                        Payload: filteredResult,
                    })
                );
            }


            const latest = getLatestUpdatedAt(result);

            ids.forEach(id => {
                this.arlsLedgerImportLastUpdatedAt.set(id, latest);
            });

        } catch (e) {
            console.error(e);
        }
    }
    public getARLsForLedgerImports$(ledgerImportIds: number[], reload = false) {
        // todo die der anderen ledgerimports rausfiltern
        return defer(() => from(this.fetchARlByLedgerImports(ledgerImportIds, reload))).pipe(switchMap(() => this.selectAllArlsDict()));
    }



    public getARLsForLedgerImport(ledgerImportId: number): Observable<AccountsReceivableLedgerEntity[]> {
        return defer(() => from(this._fetchARlByLedgerImport(ledgerImportId))).pipe(
            switchMap(() => this.arlsMap$),
            map((arls) => [...arls.values()].filter((a) => a.LedgerImportId == ledgerImportId)),
        );
    }

    private runningGetARLsForCommissionRequests: number[] = [];

    public getARLsForCommission(commissionId: number): Observable<AccountsReceivableLedgerEntity[]> {
        let fetched$ = new BehaviorSubject(false);
        let lastUpdatedAt: string | null = null;
        if (this.arlsCommissionLastUpdatedAt.has(commissionId)) {
            lastUpdatedAt = FrontendDate(this.arlsCommissionLastUpdatedAt.get(commissionId));
        }
        if (!this.runningGetARLsForCommissionRequests.includes(commissionId)) {
            this.runningGetARLsForCommissionRequests.push(commissionId);
            this.getARLs({
                commissionId: commissionId,
                ledgerImportIdIsNull: true,
                parentIdIsNull: true,
                updatedSince: lastUpdatedAt,
            })
                .then((result) => {
                    let m = this.arlsMap$.getValue();
                    result.forEach((arl) => {
                        m.set(arl.Id, arl);
                    });
                    this.arlsMap$.next(m);
                    this.arlsCommissionLastUpdatedAt.set(commissionId, getLatestUpdatedAt(result, this.arlsCommissionLastUpdatedAt.get(commissionId)));
                    fetched$.next(true);
                })
                .catch((err) => {
                    console.error(err);
                }).finally(() => {
                    this.runningGetARLsForCommissionRequests = this.runningGetARLsForCommissionRequests.filter((r) => r !== commissionId);
                });
        }
        return combineLatest([this.arlsMap$, fetched$.pipe(filter((f) => f === true))]).pipe(map(([arls, _]) => [...arls.values()].filter((a) => a.CommissionId == commissionId)));
    }


    getParentsQuantity(id: number, oldQuantiy: number, sourceDictionary: Dictionary<AccountsReceivableLedgerEntity>): number {
        let parent = sourceDictionary[id];
        let quantity = oldQuantiy + parent.Quantity;

        if (parent) {
            if (parent.ParentId) {
                this.getParentsQuantity(parent.ParentId, quantity, sourceDictionary);
            }
        }
        if (quantity === 0) {
            quantity = 1;
        }
        return quantity;
    }

    /**
     *
     * @param sourceArls
     * @param sourceArlDictionary muss auch die parents enthalten
     * @param ledgerImportId
     * @param customerId
     * @param commissionId
     * @param documentType
     * @param isOrder
     * kopier auch aus dem state
     */
    public async duplicateArlsWithoutChilds(
        sourceArls: {
            Id: number;
            Bezeichnung: string;
            QuantityTypeId: number;
            Quantity: number;
            QuantityFormula: string;
            BaseCost: number;
            BaseCostGross: number;
            BaseCostAllowances?: AccountsReceivableLedgerEntity['BaseCostAllowances'],
            ItemNumber?: string,
            SalaryBaseCost?: number,
        }[],
        sourceArlDictionary: Dictionary<AccountsReceivableLedgerEntity>,
        ledgerImportId: number | null | undefined,
        customerId: number | null | undefined,
        commissionId: number | null | undefined,
        documentType: LedgerImportDocumentTypes | null | undefined,
        isOrder: boolean,
    ): Promise<AccountsReceivableLedgerEntity[]>;
    public async duplicateArlsWithoutChilds(
        sourceArls: {
            Id: number;
            Bezeichnung: string;
            QuantityTypeId: number;
            Quantity: number;
            QuantityFormula: string;
            BaseCost: number;
            BaseCostGross: number;
            BaseCostAllowances?: AccountsReceivableLedgerEntity['BaseCostAllowances'],
            ItemNumber?: string,
            SalaryBaseCost?: number,
        }[],
        sourceArlDictionary: Dictionary<AccountsReceivableLedgerEntity>,
        ledgerImportId: number | null | undefined,
        customerId: number | null | undefined,
        commissionId: number | null | undefined,
        documentType: LedgerImportDocumentTypes | null | undefined,
        isOrder: boolean,
        getPayloadOnly: true,
    ): Promise<AccountsReceivableLedgerPostType[]>;
    public async duplicateArlsWithoutChilds(
        sourceArls: {
            Id: number;
            Bezeichnung: string;
            QuantityTypeId: number;
            Quantity: number;
            QuantityFormula: string;
            BaseCost: number;
            BaseCostGross: number;
            BaseCostAllowances?: AccountsReceivableLedgerEntity['BaseCostAllowances'],
            ItemNumber?: string,
            SalaryBaseCost?: number,
        }[],
        sourceArlDictionary: Dictionary<AccountsReceivableLedgerEntity>,
        ledgerImportId: number | null | undefined,
        customerId: number | null | undefined,
        commissionId: number | null | undefined,
        documentType: LedgerImportDocumentTypes | null | undefined,
        isOrder = false,
        getPayloadOnly = false,
    ): Promise<AccountsReceivableLedgerEntity[] | AccountsReceivableLedgerPostType[]> {
        console.log('duplicateArlsWithoutChilds', sourceArls);
        // const sourceDictionary = await firstValueFrom(getFetched$(this.store$, getAccountsReceivableLedgersFetched, getAccountsReceivableLedgerDictionary, this.arlResolver));
        const getPayload = (source: AccountsReceivableLedgerEntity): AccountsReceivableLedgerPostType => {
            let parentQuantity = 1;
            if (isOrder && source?.ParentId) {
                parentQuantity = this.getParentsQuantity(source.ParentId, 0, sourceArlDictionary);
            }
            return {
                itemNumber: source?.ItemNumber ? source.ItemNumber : AccountsReceivableLedgerEntity.autogenerateItemNumber,
                quantity: isOrder && source?.Quantity ? source?.Quantity * parentQuantity : source?.Quantity || 0,
                tax: source.Tax,
                currencyCode: source.CurrencyCode,
                bookingText: source.BookingText,
                baseCost: isOrder ? (source?.CostAmount ? source.CostAmount : source.BaseCost) : source.BaseCost || 0,
                baseCostGross: isOrder ? 0 : source.BaseCostGross || 0,
                costAmount: source.CostAmount,
                ledgerImportId: ledgerImportId !== undefined ? ledgerImportId : source.LedgerImportId,
                documentType: documentType !== undefined ? documentType : source.DocumentType,
                customerId: customerId !== undefined ? customerId : source.CustomerId,
                commissionId: commissionId !== undefined ? commissionId : source.CommissionId,
                information: source.Information,
                quantityTypeId: source.QuantityTypeId,
                multiplikator: source.Multiplier,
                customProperties: JSON.stringify({
                    ...source.CustomProperties,
                }),
                isVisible: source.IsVisible,
                longInformation: source.Longtext,
                showLongInformation: source.ShowLongtext,
                type: source.Type as any,
                resourceId: source.ResourceId,
                materialId: source.MaterialId,
                jobSpecificationId: source.JobSpecificationId,
                isOptional: source.IsOptional,
                isCrossedOut: source.CrossedOut,
                hideInGroupSummary: source.HideInGroupSummary,
                baseCostAllowances: source.BaseCostAllowances?.map((a) => ({
                    percent: a.percent,
                    type: a.arlType,
                    title: a.title,
                })),
                costAmountAllowance: source.CostAmountAllowance,

                useQuantityHistory: source.UseQuantityHistory,
                showQuantityHistory: source.ShowQuantityHistory,
                quantityHistories: source.QuantityHistory ? source.QuantityHistory.map((h) => ({ date: h.date ? FrontendDate(h.date) : null, quantityDelta: h.quantityDelta })) : [],

                lvPartieHEur: source.LVPartieHEur,
                lvSoEkRabatt: source.LVSoEkRabatt,
                lVFlEk: source.LVFlEk,
                lVGEK: source.LVGEK,
                lVSoEk: source.LVSoEk,
                lVMinutes: source.LVMinutes,
                lVSoAntEk: source.LVSoAntEk,
                quantityFormula: source.QuantityFormula,
                gaps: source.Gap,
                lohnBaseCostPart: source.SalaryBaseCost,
            };
        };
        const arlsToAdd: AccountsReceivableLedgerPostType[] = [];
        const arlsToAddSources: AccountsReceivableLedgerEntity[] = [];
        function addArls(source: AccountsReceivableLedgerEntity) {
            arlsToAdd.push(getPayload(source));
            arlsToAddSources.push(source);
        }

        sourceArls.forEach((arlOutput) => {
            const src = sourceArlDictionary[arlOutput.Id];
            let modifiableSrc: AccountsReceivableLedgerEntity = src.Clone({
                Quantity: arlOutput.Quantity,
                QuantityFormula: arlOutput.QuantityFormula,
                QuantityTypeId: arlOutput.QuantityTypeId,
                Information: arlOutput.Bezeichnung,
                BaseCost: arlOutput.BaseCost,
                BaseCostAllowances: arlOutput.BaseCostAllowances !== undefined ? arlOutput.BaseCostAllowances : src.BaseCostAllowances,
                ItemNumber: arlOutput.ItemNumber !== undefined ? arlOutput.ItemNumber : src.ItemNumber,
                SalaryBaseCost: arlOutput.SalaryBaseCost !== undefined ? arlOutput.SalaryBaseCost : src.SalaryBaseCost,
            });

            addArls(modifiableSrc);
        });
        if (getPayloadOnly) {
            return arlsToAdd;
        }
        const responseArls = await this.addArls(arlsToAdd);
        return responseArls;
    }

    /**
     *
     * @param sourceArls
     * @param sourceArlDictionary muss auch children enthalten
     * @param ledgerImportId
     * @param customerId
     * @param commissionId
     * @param documentType
     * @param addOnlySourceChilds legt nur die übergeben arls an nicht alle childs
     */
    public async duplicateArlsFromState(
        sourceArls: {
            Id: number;
            Bezeichnung: string;
            QuantityTypeId: number;
            Quantity: number;
            QuantityFormula: string;
            BaseCost: number;
            BaseCostGross: number;
            BaseCostAllowances?: AccountsReceivableLedgerEntity['BaseCostAllowances'],
            ItemNumber?: string;
            SalaryBaseCost?: number;

            setPriceFrozen?: boolean;
            /**
             * only necessary if setPriceFrozen
             */
            getARLCalculationPreChargeByTypes?: () => LedgerImportARLCalculationPreChargeByType[];
        }[],
        sourceArlDictionary: Dictionary<AccountsReceivableLedgerEntity>,
        ledgerImportId: number | null | undefined,
        customerId: number | null | undefined,
        commissionId: number | null | undefined,
        documentType: LedgerImportDocumentTypes | null | undefined,
        addOnlySourceChilds = false,
    ) {
        console.log('duplicateArlsFromState', sourceArls);
        // const sourceDictionary = await firstValueFrom(getFetched$(this.store$, getAccountsReceivableLedgersFetched, getAccountsReceivableLedgerDictionary, this.arlResolver));
        const getChildPayload = (source: AccountsReceivableLedgerEntity): AccountsReceivableLedgerPostType => {
            return {
                itemNumber: source.ItemNumber,
                quantity: source.Quantity || 0,
                tax: source.Tax,
                currencyCode: source.CurrencyCode,
                bookingText: source.BookingText,
                baseCost: source.BaseCost || 0,
                baseCostGross: source.BaseCostGross || 0,
                costAmount: source.CostAmount,
                ledgerImportId: ledgerImportId !== undefined ? ledgerImportId : source.LedgerImportId,
                documentType: documentType !== undefined ? documentType : source.DocumentType,
                customerId: customerId !== undefined ? customerId : source.CustomerId,
                commissionId: commissionId !== undefined ? commissionId : source.CommissionId,
                information: source.Information,
                quantityTypeId: source.QuantityTypeId,
                multiplikator: source.Multiplier,
                customProperties: JSON.stringify({
                    ...source.CustomProperties,
                }),
                isVisible: source.IsVisible,
                longInformation: source.Longtext,
                showLongInformation: source.ShowLongtext,
                // parentId: parentId,
                type: source.Type as any,
                resourceId: source.ResourceId,
                materialId: source.MaterialId,
                jobSpecificationId: source.JobSpecificationId,
                isOptional: source.IsOptional,
                isCrossedOut: source.CrossedOut,
                hideInGroupSummary: source.HideInGroupSummary,
                baseCostAllowances: source.BaseCostAllowances?.map((a) => ({
                    percent: a.percent,
                    type: a.arlType,
                    title: a.title,
                })),
                inheritFromChildren: source.InheritFromChildren,
                costAmountAllowance: source.CostAmountAllowance,

                useQuantityHistory: source.UseQuantityHistory,
                showQuantityHistory: source.ShowQuantityHistory,
                quantityHistories: source.QuantityHistory ? source.QuantityHistory.map((h) => ({ date: h.date ? FrontendDate(h.date) : null, quantityDelta: h.quantityDelta })) : [],

                lvPartieHEur: source.LVPartieHEur,
                lvSoEkRabatt: source.LVSoEkRabatt,
                lVFlEk: source.LVFlEk,
                lVGEK: source.LVGEK,
                lVSoEk: source.LVSoEk,
                lVMinutes: source.LVMinutes,
                lVSoAntEk: source.LVSoAntEk,
                quantityFormula: source.QuantityFormula,
                gaps: source.Gap,
                surchargeOverride: source.SurchargeOverride,
                fixedPrices: source.FixedPrices,
                lohnBaseCostPart: source.SalaryBaseCost,
            };
        };
        const childsToAdd: AccountsReceivableLedgerPostType[] = [];
        const childsToAddSources: AccountsReceivableLedgerEntity[] = [];
        function addChilds(parent: AccountsReceivableLedgerEntity) {
            if (!parent) {
                console.log(`addChilds -> ${parent?.Id}`);
                console.log('addChilds called with undefined parent');
                return;
            }


            const overrides = sourceArls.find((arl) => arl.Id === parent.Id);
            let source: AccountsReceivableLedgerEntity;
            if (!!overrides) {
                source = parent.Clone({
                    Information: overrides.Bezeichnung,
                    QuantityTypeId: overrides.QuantityTypeId,
                    Quantity: overrides.Quantity,
                    QuantityFormula: overrides.QuantityFormula,
                    BaseCost: overrides.BaseCost,
                    BaseCostGross: overrides.BaseCostGross,
                    BaseCostAllowances: overrides.BaseCostAllowances !== undefined ? overrides.BaseCostAllowances : parent.BaseCostAllowances,
                    ItemNumber: overrides.ItemNumber !== undefined ? overrides.ItemNumber : parent.ItemNumber,
                    SalaryBaseCost: overrides.SalaryBaseCost !== undefined ? overrides.SalaryBaseCost : parent.SalaryBaseCost,
                });
            } else {
                source = parent.Clone();
            }
            if (source.isBookingPosition && !source.FixedPrices) {

                const parentOverride = () =>  sourceArls.find((arl) => arl.Id === source.ParentId);
                console.log('### clone', {source, overrides, parent: sourceArlDictionary[source.ParentId], parentOverride: parentOverride()});
                // is for calculation
                if (source.ParentId && sourceArlDictionary[source.ParentId].Type !== AccountsReceivableLedgerTypeEnum.Group && parentOverride()?.setPriceFrozen) {
                    const preCharge =  parentOverride().getARLCalculationPreChargeByTypes().find(c => c.arlType === source.Type)?.percentage || 0;
                    const cost = getARLCostAmountWithAllowance(source) || 0;
                    console.log('### , preCharge, cost', preCharge, cost);
                    source.CostAmount = cost + (cost * preCharge);
                    source.CostAmountAllowance = null;
                } else if (overrides?.setPriceFrozen) {
                    source.FixedPrices = true;
                    if (source.InheritFromChildren && source.Type !== AccountsReceivableLedgerTypeEnum.Group) {
                        source.InheritFromChildren = false;
                        source.CostAmount = 0;
                        const calcChildren = source.ArlIds?.map(cId => sourceArlDictionary[cId]);
                        calcChildren?.forEach(child => {
                            const preCharge = overrides.getARLCalculationPreChargeByTypes().find(c => c.arlType === child.Type)?.percentage || 0;
                            const cost = getARLCostAmountWithAllowance(child) || 0;
                            const ekWithPreCharge = cost + (cost * preCharge);
                            source.CostAmount += (ekWithPreCharge * (child.Quantity || 0));
                        });
                    }
                }
            }


            childsToAdd.push(getChildPayload(source));
            childsToAddSources.push(source);
            // console.log('sourceId' + source.Id, source.ArlIds);
            source.ArlIds?.forEach((child) => {
                if(addOnlySourceChilds && sourceArlDictionary[child]?.ParentId != null && sourceArlDictionary[sourceArlDictionary[child]?.ParentId].Type === AccountsReceivableLedgerTypeEnum.Group) {
                    const foundArl = sourceArls.find((arl) => arl.Id === child);
                    if(foundArl) {
                        addChilds(sourceArlDictionary[child]);
                    }
                }else{
                addChilds(sourceArlDictionary[child]);
                }
            });
        }
        sourceArls.forEach((arlOutput) => {
            const src = sourceArlDictionary[arlOutput.Id];

            if (!src.ParentId || sourceArls.every((a) => a.Id !== src.ParentId)) {
                const modifiableSrc = src.Clone();
                modifiableSrc.ParentId = null;
                addChilds(modifiableSrc);
            }
        });
        // addChilds(sourceArl, ARL_DEFAULT_VALUES.isVisible);
        const responseArls = await this.addArls(childsToAdd);
        const responseArlsIds = responseArls.map((r) => r.Id);

        const sourceIdToIndex = new Map(
            childsToAddSources.map((src, idx) => [src.Id, idx])
        );
        const responseArlsMap = new Map(
            responseArls.map((arl) => [arl.Id, arl])
        );

        const arlsToUpdate: AccountsReceivableLedgerPatchType[] = [];

        childsToAddSources.forEach((src, i) => {
            // const childCount = src.ArlIds?.length || 0;
            const srcChildIds = src.ArlIds;
            if (srcChildIds?.length > 0) {
                const targetChildIds = srcChildIds.flatMap((id) => {
                    const srcChildIndex = sourceIdToIndex.get(id);
                    return srcChildIndex !== undefined ? [responseArls[srcChildIndex].Id] : [];
                }); //responseArlsIds.slice(i + 1, i + 1 + childCount);
                const targetId = responseArlsIds[i];
                const payloadExists = arlsToUpdate.find((a) => a.id === targetId);
                if (payloadExists) {
                    payloadExists.arlIds = targetChildIds;
                } else {
                    arlsToUpdate.push({
                        id: targetId,
                        arlIds: targetChildIds,
                        multiplikator: responseArls[i].Multiplier,
                    });
                }
                targetChildIds.forEach((cId) => {
                    arlsToUpdate.push({
                        id: cId,
                        parentId: targetId,
                        multiplikator: responseArlsMap.get(cId)?.Multiplier,
                    });
                });
            }
        });
        const changedArls = arlsToUpdate.length ? await this.changeArls(arlsToUpdate) : [];
        return responseArls.map((r, i) => changedArls.find((c) => c.Id === r.Id) || r);
    }

    public async duplicateArlWithChildren(
        sourceArl: arlTableDataArlForm['value'],
        getCalcZuschlFromParentArlId: (parentArlId: number) => ArlCalculationSurcharge<calculationArlForm | calculationArlTemplateForm>[],
        defaultTax: number,
        ledgerImportId: number,
    ) {
        const _getChildPayload = (
            sourceChildArl: PickCommon<typeof sourceArl, calculationArlFormGroup['value']>,
            valuesMissingInForm: Pick<AccountsReceivableLedgerPostType, 'isVisible' | 'multiplikator' | 'ledgerImportId'>,
            zuschlForCalculationArl?: ArlCalculationSurcharge<calculationArlForm | calculationArlTemplateForm>[],
        ): AccountsReceivableLedgerPostType => {
            return {
                itemNumber: sourceChildArl.ItemNumber,
                quantity: sourceChildArl.Quantity || 0,
                // currencyCode: sourceChildArl.CurrencyCode,
                bookingText: sourceChildArl.BookingText,
                baseCost: zuschlForCalculationArl ? baseCostFromFormValue(sourceChildArl, zuschlForCalculationArl) : sourceChildArl.BaseCost || 0,
                baseCostGross: sourceChildArl.BaseCostGross,
                costAmount: sourceChildArl.CostAmount,
                ledgerImportId: valuesMissingInForm.ledgerImportId,
                // documentType: sourceChildArl.DocumentType,
                // customerId: sourceChildArl.CustomerId,
                // commissionId: sourceChildArl.CommissionId,
                information: sourceChildArl.Information,
                quantityTypeId: sourceChildArl.QuantityTypeId,
                multiplikator: valuesMissingInForm.multiplikator, //todo multiplikator ganz ausbauen oder in form einbauen //sourceChildArl.Multiplier,
                // customProperties: JSON.stringify({
                //     cp,
                //     // ...sourceChildArl.CustomProperties,
                //     // rootTemplateId: sourceChildArl.a,
                // }),
                isVisible: valuesMissingInForm.isVisible,
                // parentId: parentId,
                type: sourceChildArl.Type as any,
                resourceId: sourceChildArl.ResourceId,
                materialId: sourceChildArl.MaterialId,
                jobSpecificationId: sourceChildArl.JobSpecificationId,
                isOptional: sourceChildArl.IsOptional,
                isCrossedOut: sourceChildArl.CrossedOut,
                hideInGroupSummary: sourceChildArl.HideInGroupSummary,
                quantityFormula: sourceChildArl.QuantityFormula,
                surchargeOverride: sourceChildArl.SurchargeOverride,
            };
        };



        const getChildPayloadCalculation = (
            sourceChildArl: calculationArlFormGroup['value'],
            valuesMissingInForm: Pick<AccountsReceivableLedgerPostType, 'isVisible' | 'multiplikator' | 'itemNumber' | 'ledgerImportId'>,
            zuschlForCalculationArl?: ArlCalculationSurcharge<calculationArlForm | calculationArlTemplateForm>[],
        ): AccountsReceivableLedgerPostType => {
            const cp: ARLCustomProperties = {};
            if (isNotNullOrUndefined(sourceChildArl.IndividuallyCostCalculation)) {
                cp.individuallyCostCalculation = sourceChildArl.IndividuallyCostCalculation;
            }

            return {
                ..._getChildPayload(sourceChildArl, valuesMissingInForm, zuschlForCalculationArl),
                tax: defaultTax, // fix fürn nicht angelegte kalkulierte arls
                costAmountAllowance: sourceChildArl.CostAmountAllowance,

                customProperties: JSON.stringify(cp),
            };
        };


        const getChildPayload = (
            sourceChildArl: typeof sourceArl,
            valuesMissingInForm: Pick<AccountsReceivableLedgerPostType, 'isVisible' | 'multiplikator' | 'itemNumber' | 'ledgerImportId'>,
            zuschlForCalculationArl?: ArlCalculationSurcharge<calculationArlForm | calculationArlTemplateForm>[],
        ): AccountsReceivableLedgerPostType => {
            const cp: ARLCustomProperties = {
                ZuschlaegeDefault: sourceChildArl.ZuschlaegeDefault,
                ZuschlaegeResourcen: sourceChildArl.ZuschlaegeResourcen,
                ZuschlaegeMaterials: sourceChildArl.ZuschlaegeMaterials,
                ZuschlaegeEmployee: sourceChildArl.ZuschlaegeEmployee,
                ZuschlaegeExternalService: sourceChildArl.ZuschlaegeExternalService,
                ZuschlaegeOther: sourceChildArl.ZuschlaegeOther,
            };

            return {
                ..._getChildPayload(sourceChildArl, valuesMissingInForm, zuschlForCalculationArl),
                tax: isNotNullOrUndefined(sourceChildArl.Tax) ? sourceChildArl.Tax : defaultTax, // fix fürn nicht angelegte kalkulierte arls
                longInformation: sourceChildArl.Longtext,
                showLongInformation: sourceChildArl.ShowLongtext,
                baseCostAllowances: sourceChildArl.BaseCostAllowances?.map((a) => ({
                    percent: a.percent,
                    type: a.arlType,
                    title: a.title,
                })),
                inheritFromChildren: sourceChildArl.InheritFromChildren,
                fixedPrices: sourceChildArl.FixedPrices,
                lohnBaseCostPart: sourceChildArl.SalaryBaseCost,
                customProperties: JSON.stringify(cp),
            };
        };


        const childsToAdd: AccountsReceivableLedgerPostType[] = [];
        const childsToAddSources: typeof sourceArl[] = [];
        function addChilds(source: typeof sourceArl, isVisible: boolean, zuschl?: ArlCalculationSurcharge<calculationArlForm | calculationArlTemplateForm>[]) {
            childsToAdd.push(getChildPayload(source, { isVisible, ledgerImportId, multiplikator: 1 }, zuschl));
            childsToAddSources.push(source);
            source.childArls?.forEach(async (child) => {
                addChilds(child, ARL_DEFAULT_VALUES.isVisible);
            });
            source.childArlsCalculation?.forEach(async (child) => {
                const z = getCalcZuschlFromParentArlId(source.Id);
                // addChilds(child, child.IsVisible, z);
                childsToAdd.push(getChildPayloadCalculation(child, { isVisible: child.IsVisible, ledgerImportId, multiplikator: 1 }, z));
                childsToAddSources.push(child);
            });
        }
        addChilds(sourceArl, ARL_DEFAULT_VALUES.isVisible);
        const responseArls = await this.addArls(childsToAdd);
        const responseArlsIds = responseArls.map((r) => r.Id);

        const arlsToUpdate: AccountsReceivableLedgerPatchType[] = [];
        childsToAddSources.forEach((src, i) => {
            const childCount = (src.childArls?.length || 0) + (src.childArlsCalculation?.length || 0);
            if (childCount > 0) {
                // const targetChildIds = responseArlsIds.slice(i + 1, i + 1 + childCount); // hier sind die falschen children drin wenn die auch unterchildren haben
                const targetChildIds = childsToAddSources
                    .map((p, i) => {
                        if (p.ParentId === src.Id) {
                            return responseArlsIds[i];
                        }
                        return null;
                    })
                    .filter((p) => p !== null);
                const targetId = responseArlsIds[i];
                const payloadExists = arlsToUpdate.find((a) => a.id === targetId);
                if (payloadExists) {
                    payloadExists.arlIds = targetChildIds;
                } else {
                    arlsToUpdate.push({
                        id: targetId,
                        arlIds: targetChildIds,
                        multiplikator: responseArls[i].Multiplier,
                    });
                }
                targetChildIds.forEach((cId) => {
                    arlsToUpdate.push({
                        id: cId,
                        parentId: targetId,
                        multiplikator: responseArls.find((r) => r.Id === cId)?.Multiplier,
                    });
                });
            }
        });
        const changedArls = arlsToUpdate.length ? await this.changeArls(arlsToUpdate) : [];
        return responseArls.map((r, i) => changedArls.find((c) => c.Id === r.Id) || r);
    }

    // public getArlsForCustomerCreateNewLedgerImport(customerId: number): Observable<AccountsReceivableLedgerEntity[]> {
    //     return getFetched$(this.store$, getAccountsReceivableLedgersFetched, getAccountsReceivableLedgers).pipe(map((arls) => arls.filter((b) => b.CustomerId === customerId && !b.LedgerImportId)));
    // }

    public getAllArls(resolve: boolean): Observable<AccountsReceivableLedgerEntity[]> {
        if (resolve) {
            this.arlResolver.resolve();
        }
        return getFetched$(this.store$, getAccountsReceivableLedgersFetched, getAccountsReceivableLedgers);
    }

    public selectAllArlsDict(): Observable<Dictionary<AccountsReceivableLedgerEntity>> {
        return this.store$.select(getAccountsReceivableLedgerDictionary);
    }
    public getAllArlsAsDict(resolve: boolean): Observable<Dictionary<AccountsReceivableLedgerEntity>> {
        if (resolve) {
            this.arlResolver.resolve();
        }
        return getFetched$(this.store$, getAccountsReceivableLedgersFetched, getAccountsReceivableLedgerDictionary);
    }
    //
    // public getArlsFromLedgerImportAsDict(ledgerImportId: number): Observable<Dictionary<AccountsReceivableLedgerEntity>>{
    //     // todo christian wie ist das gedacht? in arlsMap$ kommen ja nur neue Werde wenn man nochmal am Backend abruft, beim bearbeiten / löschen usw nicht.
    //     return this.arlsMap$.pipe(map(arls => {
    //         let d =  {};
    //         arls.forEach(a => {
    //             if (a.LedgerImportId === ledgerImportId) {
    //                 d[a.Id] = a
    //             }
    //         })
    //         return d as Dictionary<AccountsReceivableLedgerEntity>;
    //     }));
    // }

    /**
     * @param ledgerImportDocumentType
     * @param materialId
     * gibt alle arls zurück welch die entsprechende materialId haben und in einem LedgerImport mit dem passenden documentType stehen, kalkulations-arls ausgeschlossen
     */
    public getArlsForMaterialList(materialId: number, ledgerImportDocumentType: LedgerImportDocumentTypes): Observable<AccountsReceivableLedgerEntity[]> {
        // todo: muss Backend Filtern,
        return combineLatest([
            getFetched$(this.store$, getAccountsReceivableLedgersFetched, getAccountsReceivableLedgers),
            getFetched$(this.store$, getAccountsReceivableLedgersFetched, getAccountsReceivableLedgerDictionary, this.arlResolver),
            getFetched$(this.store$, getLedgerImportsFetched, getLedgerImportDictionary),
        ]).pipe(
            map(([arls, allArls, ledgerImports]) =>
                arls.filter((arl) => {
                    if (!(arl.MaterialId === materialId) || !arl.LedgerImportId) {
                        return false;
                    }

                    const li = ledgerImports[arl.LedgerImportId];
                    if (!li || ledgerImportDocumentType !== li.DocumentType) {
                        return false;
                    }
                    if (li.AccountsReceivableLedgerIds.includes(arl.Id)) {
                        return true;
                    }
                    return li.AccountsReceivableLedgerIds.some((arlId) => _getArlChildren(arlId, allArls).some((a) => a.Id === arl.Id));
                }),
            ),
        );
    }
}

const getPayloadFromArl = (source: Pick<AccountsReceivableLedgerEntity,
    'ItemNumber' | 'Quantity' | 'Tax' | 'CurrencyCode' | 'BookingText' | 'BaseCost'
    | 'BaseCostGross' | 'CostAmount' | 'LedgerImportId' | 'DocumentType' | 'CustomerId'
    | 'CommissionId' | 'Information' | 'QuantityTypeId' | 'Multiplier'
| 'CustomProperties'
| 'IsVisible'
| 'Longtext'
| 'ShowLongtext'
| 'Type'
| 'ResourceId'
| 'MaterialId'
| 'JobSpecificationId'
| 'IsOptional'
| 'CrossedOut'
| 'HideInGroupSummary'
| 'BaseCostAllowances'
| 'InheritFromChildren'
| 'CostAmountAllowance'
| 'UseQuantityHistory'
| 'ShowQuantityHistory'
| 'QuantityHistory'
| 'LVPartieHEur'
| 'LVSoEkRabatt'
| 'LVFlEk'
| 'LVGEK'
| 'LVSoEk'
| 'LVMinutes'
| 'LVSoAntEk'
| 'QuantityFormula'
| 'Gap'
| 'SurchargeOverride'
| 'FixedPrices'
| 'SalaryBaseCost'
>): AccountsReceivableLedgerPostType => {
    return {
        itemNumber: source.ItemNumber,
        quantity: source.Quantity || 0,
        tax: source.Tax,
        currencyCode: source.CurrencyCode,
        bookingText: source.BookingText,
        baseCost: source.BaseCost || 0,
        baseCostGross: source.BaseCostGross || 0,
        costAmount: source.CostAmount,
        ledgerImportId: source.LedgerImportId,
        documentType: source.DocumentType,
        customerId: source.CustomerId,
        commissionId: source.CommissionId,
        information: source.Information,
        quantityTypeId: source.QuantityTypeId,
        multiplikator: source.Multiplier,
        customProperties: JSON.stringify({
            ...source.CustomProperties,
        }),
        isVisible: source.IsVisible,
        longInformation: source.Longtext,
        showLongInformation: source.ShowLongtext,
        // parentId: parentId,
        type: source.Type as any,
        resourceId: source.ResourceId,
        materialId: source.MaterialId,
        jobSpecificationId: source.JobSpecificationId,
        isOptional: source.IsOptional,
        isCrossedOut: source.CrossedOut,
        hideInGroupSummary: source.HideInGroupSummary,
        baseCostAllowances: source.BaseCostAllowances?.map((a) => ({
            percent: a.percent,
            type: a.arlType,
            title: a.title,
        })),
        inheritFromChildren: source.InheritFromChildren,
        costAmountAllowance: source.CostAmountAllowance,

        useQuantityHistory: source.UseQuantityHistory,
        showQuantityHistory: source.ShowQuantityHistory,
        quantityHistories: source.QuantityHistory ? source.QuantityHistory.map((h) => ({ date: h.date ? FrontendDate(h.date) : null, quantityDelta: h.quantityDelta })) : [],

        lvPartieHEur: source.LVPartieHEur,
        lvSoEkRabatt: source.LVSoEkRabatt,
        lVFlEk: source.LVFlEk,
        lVGEK: source.LVGEK,
        lVSoEk: source.LVSoEk,
        lVMinutes: source.LVMinutes,
        lVSoAntEk: source.LVSoAntEk,
        quantityFormula: source.QuantityFormula,
        gaps: source.Gap,
        surchargeOverride: source.SurchargeOverride,
        fixedPrices: source.FixedPrices,
        lohnBaseCostPart: source.SalaryBaseCost,
    };
};
